import { auth } from '@/lib/supabase';

const TOKEN_PARAM_KEYS = [
  'access_token',
  'refresh_token',
  'expires_in',
  'token_type',
  'provider_token',
  'code',
];

const CALLBACK_STORAGE_KEY = 'odv-auth-callback-meta';
const GLOBAL_FLAG = '__odvAuthCallbackHandled';

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const parseParams = () => {
  if (!hasWindow()) {
    return {
      merged: new URLSearchParams(),
      callbackType: null,
    };
  }

  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const searchParams = new URLSearchParams(window.location.search);
  const merged = new URLSearchParams();

  hashParams.forEach((value, key) => merged.set(key, value));
  searchParams.forEach((value, key) => merged.set(key, value));

  const callbackType = merged.get('type') || merged.get('event') || null;

  return { merged, callbackType };
};

export const clearAuthParamsFromUrl = () => {
  if (!hasWindow()) return;

  const url = new URL(window.location.href);
  TOKEN_PARAM_KEYS.forEach((param) => url.searchParams.delete(param));

  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const filteredHash = [];
  hashParams.forEach((value, key) => {
    if (!TOKEN_PARAM_KEYS.includes(key)) {
      filteredHash.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  const newHash = filteredHash.length ? `#${filteredHash.join('&')}` : '';
  const newUrl = `${url.pathname}${url.search}${newHash}`;

  window.history.replaceState({}, document.title, newUrl);
};

const storeCallbackMetadata = (metadata) => {
  if (!hasWindow()) return;

  try {
    sessionStorage.setItem(CALLBACK_STORAGE_KEY, JSON.stringify(metadata));
    window.__odvAuthCallbackMeta = metadata;
  } catch (error) {
    console.warn('[authCallback] Failed to store callback metadata', error);
  }
};

export const getAuthCallbackMetadata = () => {
  if (!hasWindow()) return null;

  if (window.__odvAuthCallbackMeta) {
    return window.__odvAuthCallbackMeta;
  }

  const raw = sessionStorage.getItem(CALLBACK_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    window.__odvAuthCallbackMeta = parsed;
    return parsed;
  } catch (error) {
    console.warn('[authCallback] Failed to parse callback metadata', error);
    return null;
  }
};

export const consumeAuthCallbackMetadata = () => {
  const metadata = getAuthCallbackMetadata();

  if (hasWindow()) {
    sessionStorage.removeItem(CALLBACK_STORAGE_KEY);
    delete window.__odvAuthCallbackMeta;
  }

  return metadata;
};

export const handleAuthCallbackFromUrl = async () => {
  if (!hasWindow()) return null;
  if (window[GLOBAL_FLAG]) return null;

  const { merged, callbackType } = parseParams();
  const accessToken = merged.get('access_token');
  const refreshToken = merged.get('refresh_token');
  const code = merged.get('code');

  if (!accessToken && !refreshToken && !code) {
    return null;
  }

  window[GLOBAL_FLAG] = true;

  let session = null;

  // Helper to add timeout to auth operations
  const withTimeout = (promise, timeoutMs = 10000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Auth operation timed out')), timeoutMs)
      ),
    ]);
  };

  try {
    // For OAuth PKCE flow with code parameter:
    // AuthContext handles the code exchange manually via auth.exchangeCodeForSession()
    // We just signal that we detected the code and let AuthContext handle it
    if (code) {
      console.log('[authCallback] OAuth code detected - AuthContext will handle exchange');
      // Don't do anything here - AuthContext.bootstrapAuth handles it
      return null;
    }

    // For implicit flow (access_token in URL), check if session exists
    if (accessToken) {
      console.log('[authCallback] Access token detected, checking for session...');
      try {
        const { data } = await withTimeout(auth.getSession(), 5000);
        if (data?.session?.user) {
          console.log('[authCallback] Session found');
          clearAuthParamsFromUrl();
          return { session: data.session, callbackType: callbackType || 'existing' };
        }
      } catch (e) {
        console.log('[authCallback] getSession failed:', e.message);
      }
    }

    // For access_token/refresh_token in URL (implicit flow), we can set the session
    if (accessToken && refreshToken) {
      console.log('[authCallback] Setting session from URL tokens');
      const { data, error } = await withTimeout(
        auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
      );

      if (error) {
        throw error;
      }

      session = data.session;
    }

    if (session?.user) {
      storeCallbackMetadata({
        type: callbackType || session.user.app_metadata?.provider || 'unknown',
        timestamp: Date.now(),
      });
    }

    clearAuthParamsFromUrl();
    return { session, callbackType: callbackType || null };
  } catch (error) {
    console.warn('[authCallback] Auth callback failed, clearing params:', error.message);
    // ✅ Always clear auth params from URL on failure to prevent loop
    clearAuthParamsFromUrl();
    window[GLOBAL_FLAG] = false;
    throw error;
  }
};

